export type SortDirection = "asc" | "desc";

export interface SortState<T extends string> {
  key: T;
  direction: SortDirection;
}

export function toggleSortDirection(direction: SortDirection): SortDirection {
  return direction === "asc" ? "desc" : "asc";
}

export function updateSortState<T extends string>(
  currentState: SortState<T>,
  newKey: T
): SortState<T> {
  if (currentState.key === newKey) {
    return {
      key: newKey,
      direction: toggleSortDirection(currentState.direction),
    };
  }
  return {
    key: newKey,
    direction: "asc",
  };
}

export function sortData<T extends Record<string, any>>(
  data: T[],
  sortKey: keyof T,
  direction: SortDirection
): T[] {
  return [...data].sort((a, b) => {
    const aValue = a[sortKey];
    const bValue = b[sortKey];

    // Handle null/undefined values
    if (aValue == null) return direction === "asc" ? -1 : 1;
    if (bValue == null) return direction === "asc" ? 1 : -1;

    // Handle string comparison (case-insensitive)
    if (typeof aValue === "string" && typeof bValue === "string") {
      const comparison = aValue.localeCompare(bValue, undefined, {
        sensitivity: "base",
      });
      return direction === "asc" ? comparison : -comparison;
    }

    // Handle boolean values
    if (typeof aValue === "boolean" && typeof bValue === "boolean") {
      const comparison = aValue === bValue ? 0 : aValue ? 1 : -1;
      return direction === "asc" ? comparison : -comparison;
    }

    // Handle number comparison
    const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    return direction === "asc" ? comparison : -comparison;
  });
}

export function getSortIcon(
  currentKey: string,
  sortState: { key: string; direction: SortDirection }
): string {
  if (currentKey !== sortState.key) return "↕️";
  return sortState.direction === "asc" ? "↑" : "↓";
}
